import { Button, Select, TextInput } from '@octano/global-ui';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { ModalConfirmation } from '../../../../../components/modals/Confirmation';
import { useValidations } from '../../../../../hooks/useValidations';
import {
  AdmissionScope,
  AdmissionViaOption,
  AvailableOffer,
  Offer,
} from '../../types';
import { getSelectedAdmissionScope } from '../../utils';

const PREFIX = 'offer_modal';

interface Props {
  offer?: Offer;
  availableOffers: AvailableOffer[];
  admissionVias: AdmissionViaOption[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const DEFAULT_VALUES = {
  [`${PREFIX}_enrollmentType`]: null,
  [`${PREFIX}_quota`]: null,
  [`${PREFIX}_offerQuota`]: null,
  [`${PREFIX}_offerId`]: null,
  [`${PREFIX}_admissionViaId`]: null,
  [`${PREFIX}_admissionViaName`]: null,
};

export default function AdmissionProcessOfferForm({
  offer,
  availableOffers,
  admissionVias,
  onSubmit,
  onCancel,
  isSubmitting,
}: Props) {
  const { t } = useTranslation('academic-offer/admission-processes');
  const { msgValidations } = useValidations();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingData, setPendingData] = useState<any>(null);
  const isEdit = !!offer;

  const texts = useMemo(() => {
    const prefix = 'offersList.modalForm';
    return {
      title: isEdit ? t(`${prefix}.title`) : t('offersTable.addOffer'),
      enrollmentType: t(`${prefix}.enrollmentType`),
      quota: t('offersTable.quota'),
      offerQuota: t(`${prefix}.offerQuota`),
      save: isEdit ? t(`${prefix}.save`) : t('common.add'),
      close: isEdit ? t(`${prefix}.close`) : t('common.cancel'),
      offer: t('offersTable.offer'),
      admissionVia: t('offersList.modalForm.admissionVia'),
    };
  }, [t, isEdit]);

  const { control, handleSubmit, reset, watch, setValue } = useForm<any>({
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    const commonValues =
      isEdit && offer
        ? {
            [`${PREFIX}_enrollmentType`]: getSelectedAdmissionScope(
              offer?.admissionScopes ?? [],
              t,
            ),
            [`${PREFIX}_quota`]: offer?.quota?.toString() ?? null,
            [`${PREFIX}_offerQuota`]: offer?.offerQuota?.toString() ?? null,
            [`${PREFIX}_offerId`]: {
              label: `${offer.studyPlanName} - ${offer.campusName} - ${offer.scheduleName}`,
              value: offer.admissionId,
            },
            [`${PREFIX}_admissionViaId`]: offer.admissionViaId
              ? {
                  label: offer.admissionViaName ?? '',
                  value: offer.admissionViaId,
                }
              : null,
            [`${PREFIX}_admissionViaName`]: offer.admissionViaName ?? '',
          }
        : {};

    reset({ ...DEFAULT_VALUES, ...commonValues });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, offer, reset, t]);

  const offerOptions = useMemo(() => {
    return availableOffers.map((o) => ({
      label: `${o.studyPlanName} - ${o.campusName} - ${o.scheduleName}`,
      value: o.id,
      isDisabled: o.isSaturated,
    }));
  }, [availableOffers]);

  const offerId = watch(`${PREFIX}_offerId`);

  const showViaField = admissionVias.length > 1;

  const selectedAvailableOffer = useMemo(() => {
    const targetId = isEdit ? offer?.offerId : offerId?.value;
    return availableOffers.find((o) => o.id === targetId);
  }, [isEdit, offer, offerId, availableOffers]);

  const viaOptions = useMemo(() => {
    const freeIds = new Set(selectedAvailableOffer?.availableViaIds ?? []);
    return admissionVias
      .filter((via) => freeIds.has(via.id))
      .map((via) => ({ label: via.name, value: via.id }));
  }, [admissionVias, selectedAvailableOffer]);

  useEffect(() => {
    if (offerId?.value) {
      const selectedOffer = availableOffers.find((o) => o.id === offerId.value);
      if (selectedOffer) {
        setValue(
          `${PREFIX}_offerQuota`,
          selectedOffer.offerQuota?.toString() ?? null,
          {
            shouldValidate: true,
          },
        );
      }
    }
  }, [offerId, availableOffers, setValue]);

  const handleFormSubmit = (data: any) => {
    const finalData = {
      ...data,
      offerId: data[`${PREFIX}_offerId`],
      admissionViaId: data[`${PREFIX}_admissionViaId`]?.value ?? null,
      quota: data[`${PREFIX}_quota`],
      offerQuota: data[`${PREFIX}_offerQuota`],
      enrollmentType: data[`${PREFIX}_enrollmentType`],
    };
    const quota = finalData.quota ? Number(finalData.quota) : 0;
    const offerQuota = selectedAvailableOffer?.offerQuota ?? 0;
    const allocatedQuota = selectedAvailableOffer?.allocatedQuota ?? 0;
    const ownQuota = isEdit ? offer?.quota ?? 0 : 0;
    const remaining = offerQuota - (allocatedQuota - ownQuota);

    // A separate-quota via adds seats on top of the offer's declared quota,
    // so there is nothing to exceed and no confirmation to ask for. With
    // exactly one via, the field is hidden (showViaField below) and
    // finalData.admissionViaId stays null even though the backend
    // auto-assigns that single via (resolveCreateAdmissionViaId), so it must
    // be looked up by position, not by the (absent) form value.
    const selectedVia =
      admissionVias.find((via) => via.id === finalData.admissionViaId) ??
      (admissionVias.length === 1 ? admissionVias[0] : undefined);
    const isSeparateQuota = selectedVia?.deductsOfferQuota === false;

    if (!isSeparateQuota && quota > remaining) {
      setPendingData(finalData);
      setShowConfirmationModal(true);
      return;
    }
    onSubmit(finalData);
  };

  const handleConfirmSubmit = () => {
    if (pendingData) {
      onSubmit(pendingData);
    }
    setShowConfirmationModal(false);
    setPendingData(null);
  };

  const handleCancelSubmit = () => {
    setShowConfirmationModal(false);
    setPendingData(null);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Row>
        <Col xs={12}>
          <h3 className="fs-22 text-center mb-3">{texts.title}</h3>
        </Col>
        <Col xs={12} className="mt-3">
          <Select
            label={texts.offer}
            name={`${PREFIX}_offerId`}
            options={offerOptions}
            control={control}
            placeholder={t('common.select')}
            disabled={isEdit}
            rules={{ required: !isEdit ? msgValidations.required : false }}
            noOptionsMessage={t('offersTable.noOffersToAdd')}
          />
        </Col>
        <Col xs={12} md={6} className="mt-3">
          <TextInput
            label={texts.quota}
            control={control}
            name={`${PREFIX}_quota`}
            rules={{
              pattern: {
                value: /^[0-9]*$/,
                message: msgValidations.invalidIntNumeric,
              },
            }}
          />
        </Col>
        <Col xs={12} md={6} className="mt-3">
          <TextInput
            label={texts.offerQuota}
            control={control}
            name={`${PREFIX}_offerQuota`}
            readOnly={true}
          />
        </Col>
        {showViaField && (
          <Col xs={12} md={6} className="mt-3">
            {isEdit && offer?.admissionViaId ? (
              <TextInput
                label={texts.admissionVia}
                control={control}
                name={`${PREFIX}_admissionViaName`}
                readOnly={true}
              />
            ) : (
              <Select
                label={texts.admissionVia}
                name={`${PREFIX}_admissionViaId`}
                options={viaOptions}
                control={control}
                placeholder={t('common.select')}
                rules={{ required: msgValidations.required }}
              />
            )}
          </Col>
        )}
        <Col xs={12} md={6} className="mt-3">
          <Select
            label={texts.enrollmentType}
            control={control}
            name={`${PREFIX}_enrollmentType`}
            options={[
              {
                label: 'Matrícula presencial',
                value: AdmissionScope.OS,
              },
              {
                label: 'Matrícula autoatendida (online)',
                value: AdmissionScope.OL,
              },
              {
                label: 'Matrícula presencial y autoatendida',
                value: `${AdmissionScope.OS},${AdmissionScope.OL}`,
              },
            ]}
            rules={{ required: msgValidations.required }}
          />
        </Col>
      </Row>
      <Row className="mt-5">
        <Col xs={6}>
          <Button
            text={texts.close}
            outlined
            onClick={onCancel}
            fullwidth
            disabled={isSubmitting}
          />
        </Col>
        <Col xs={6}>
          <Button
            text={texts.save}
            type="submit"
            fullwidth
            loading={isSubmitting}
          />
        </Col>
      </Row>
      <ModalConfirmation
        open={showConfirmationModal}
        title={''}
        subtitle={t('offersList.modalForm.quotaExceededMessage')}
        onClose={handleCancelSubmit}
        onConfirm={handleConfirmSubmit}
      />
    </form>
  );
}
